import React, {Component, ReactElement} from 'react'
import {Location} from 'react-router-dom'
import NatResidentUserRepo from '../residentUser/repository/NatResidentUserRepo'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    AuthoritySettings,
    GetResidentUserInvolvingSubDetail,
    MainUser,
    SubUser
} from '../residentUser/repository/NatGetResidentUserInvolvingSub'
import {Nullable} from '../../../app/TypeHelpers'
import {Box} from '@mui/material';
import {StyledPageTitle, StyledSecondaryButton} from '../../../components/reservations/components/CommonStyle'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {
    BlockSpacer,
    BlockSpacerPcOnly,
    BlockSpacerSpOnly,
    currentMedia,
    GridContainer,
    GridItem
} from '../../../styledComponents/ResponsiveConvenience'
import {ResidentLoginInfoSessionStorageKey} from '../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {
    AVAILABLE_OR_NOT,
    CodeMasterInterface,
    RESIDENT_USER_TYPE,
    SUB_USER_AUTHORITY_SETTING_ACCOUNT,
    SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT,
    VALID_OR_NOT
} from '../../../CodeMaster'
import {DisplayItemDividerForPageTop, DisplayItemInputTitleNoMargin} from '../../../styledComponents/DisplayItem'
import {DeleteForever} from '@mui/icons-material'
import {PlainLinkStyledBackButton, UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import {Messages} from '../../../Messages'
import NatRoomToResidentRepo from '../roomToResident/repository/NatRoomToResidentRepo'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import PartialAuthorityChecker from '../../../components/PartialAuthorityChecker';
import {AUTHORITY_ISSUE, AUTHORITY_VIEW} from '../condominiumAuthoritySetting/NatAdminCondominiumAuthorityConstant'
import NatResidentMessageDialogComponent from '../common/resident/NatResidentMessageDialogComponent'
import ResidentGeneralMeetingRepo, {
    GetUnitOwnerByLoginUserRoomResponse,
    UnitOwner
} from '../../../components/generalMeeting/ResidentGeneralMeetingRepo'
import {judgeAuthorityCheck, NatAsyncCallFunction} from '../../common/util/Natutil'

interface Props {
    condominiumCd: string,
    ridgeCd: string,
    roomNumber: string,
    seq: number,
    residentUserRepo: NatResidentUserRepo,
    roomToResidentRepo: NatRoomToResidentRepo,
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
    location: Location,
}

interface States {
    userInfo: UserInfo
    subUserInfo: Nullable<SubUser>
    subUserAuthorityList: SubUserAuthority[]
    subUserFacilityReserveAvailability: Nullable<string>
    unitOwner: Nullable<UnitOwner>
    openDeleteDialog: boolean
    isLoadCompleted: boolean
}

interface UserInfo {
    userId: string
    familyName: string
    firstName: string
    mailAddressPc: Nullable<string>
    mailAddressMb: Nullable<string>
    isSelf: boolean
    residentUserType: RESIDENT_USER_TYPE
}

interface SubUserAuthority {
    authorityName: string
    valid: SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT | SUB_USER_AUTHORITY_SETTING_ACCOUNT
}

const FUNCTION_DIV_ID_BILLING_AMOUNT = 63
const FUNCTION_DIV_ID_RECEIPT = 64
const FUNCTION_DIV_ID_ACCOUNT_INFORMATION = 65

class NatResidentUserDetailPage extends Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {
    loginUserResidentUserType: Nullable<string>
    loginUserUserId: Nullable<string>

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            userInfo: {
                userId: '',
                familyName: '',
                firstName: '',
                mailAddressPc: '',
                mailAddressMb: '',
                isSelf: false,
                residentUserType: RESIDENT_USER_TYPE.MAIN,
            },
            subUserInfo: null,
            subUserAuthorityList: [],
            subUserFacilityReserveAvailability: null,
            unitOwner: null,
            openDeleteDialog: false,
            isLoadCompleted: false
        }

        this.loginUserResidentUserType =
            SessionStorageHelper.getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_USER_RESIDENT_USER_TYPE)
        this.loginUserUserId =
            SessionStorageHelper.getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_USER_ID)

        this.handleDeleteClick = this.handleDeleteClick.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this)
        this.backButtonClick = this.backButtonClick.bind(this)
        this.changeAuthorityButtonClick = this.changeAuthorityButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {seq} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const residentUserInvolvingSubResponse = await this.getResidentUserInvolvingSub()

            if (residentUserInvolvingSubResponse) {
                const mainUser = residentUserInvolvingSubResponse.mainUser
                const subUsers = residentUserInvolvingSubResponse.subUser

                let userInfo: MainUser | SubUser
                let residentUserType: RESIDENT_USER_TYPE
                if (mainUser.seq === seq) {
                    userInfo = mainUser
                    residentUserType = RESIDENT_USER_TYPE.MAIN
                } else {
                    const targetSubUser = subUsers.find(subUser => subUser.seq === seq)
                    if (!targetSubUser) {
                        return
                    }
                    userInfo = targetSubUser
                    residentUserType = RESIDENT_USER_TYPE.SUB
                    this.setState({
                        subUserInfo: targetSubUser,
                        subUserAuthorityList: this.getSubUserAuthority(targetSubUser?.authoritySettings ?? []),
                        subUserFacilityReserveAvailability: targetSubUser.facilityReserveAvailability
                    })
                }
                this.setState({
                    userInfo: {
                        userId: userInfo.userId,
                        familyName: userInfo.familyName,
                        firstName: userInfo.firstName,
                        mailAddressPc: userInfo.mailAddressPc,
                        mailAddressMb: userInfo.mailAddressMb,
                        isSelf: userInfo.userId === this.loginUserUserId,
                        residentUserType: residentUserType
                    },
                })
            }
            if (judgeAuthorityCheck(HKConstants.CMN.AUTH.EDIT_UNIT_OWNER_CONFIRMATION)) {
                const unitOwnerResponse: Nullable<GetUnitOwnerByLoginUserRoomResponse> = await NatAsyncCallFunction(
                    this.props.residentGeneralMeetingRepo.getUnitOwnerByLoginUserRoom.bind(
                        this.props.residentGeneralMeetingRepo,
                    ),
                    this.props.navigate,
                )
                if (unitOwnerResponse) {
                    this.setState({
                        unitOwner: unitOwnerResponse.details
                    })
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getResidentUserInvolvingSub(): Promise<Nullable<GetResidentUserInvolvingSubDetail>> {
        const {residentUserRepo, eventEmitter, condominiumCd, ridgeCd, roomNumber} = this.props

        const response = await this.props.asyncCallFunction(
            residentUserRepo.getResidentUserInvolvingSub.bind(residentUserRepo, condominiumCd, ridgeCd, roomNumber),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            }
        )

        return response ? response.details : null
    }

    getSubUserAuthority(authoritySettings: AuthoritySettings[]): SubUserAuthority[] {
        const subUserAuthority: SubUserAuthority[] = []

        const receipt = authoritySettings.find(
            authoritySetting => authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_RECEIPT)
        const billingAmount = authoritySettings.find(
            authoritySetting => authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_BILLING_AMOUNT)
        const accountInformation = authoritySettings.find(
            authoritySetting => authoritySetting.functionAuthorityDivisionId === FUNCTION_DIV_ID_ACCOUNT_INFORMATION)

        if (billingAmount || receipt) {
            let valid: CodeMasterInterface
            if (receipt?.authorityId === AUTHORITY_ISSUE &&
                receipt?.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_POSSIBLE_ISSUE_POSSIBLE
            } else if (billingAmount?.authorityId === AUTHORITY_VIEW &&
                billingAmount?.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_POSSIBLE_ISSUE_IMPOSSIBLE
            } else {
                valid = SUB_USER_AUTHORITY_SETTING_BILLING_RECEIPT.BROWSE_IMPOSSIBLE
            }
            subUserAuthority.push({authorityName: '請求金額の閲覧・領収書発行', valid: valid})
        }

        if (accountInformation) {
            let valid: CodeMasterInterface
            if (accountInformation.authorityId === AUTHORITY_VIEW &&
                accountInformation.validFlag === VALID_OR_NOT.VALID.value) {
                valid = SUB_USER_AUTHORITY_SETTING_ACCOUNT.BROWSE_POSSIBLE
            } else {
                valid = SUB_USER_AUTHORITY_SETTING_ACCOUNT.BROWSE_IMPOSSIBLE
            }
            subUserAuthority.push({authorityName: '口座情報の閲覧', valid: valid})
        }

        return subUserAuthority
    }

    backButtonClick(): void {
        const {navigate, condominiumCd, ridgeCd, roomNumber} = this.props
        navigate(`/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users`)
    }

    changeAuthorityButtonClick(): void {
        const {navigate, condominiumCd, ridgeCd, roomNumber, seq} = this.props
        const {subUserInfo} = this.state

        navigate(`/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users/${seq}/authority`,
            {state: {subUserInfo: {...subUserInfo}}}
        )
    }


    handleDeleteClick(): void {
        this.setState({openDeleteDialog: true})
    }

    handleCancelClick(): void {
        this.setState({openDeleteDialog: false})
    }

    async handleDeleteOkClick(): Promise<void> {
        const {eventEmitter, roomToResidentRepo, condominiumCd, ridgeCd, roomNumber, seq} = this.props

        this.setState({openDeleteDialog: false})
        await this.props.eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                roomToResidentRepo.deleteRoomToResidentByResident.bind(roomToResidentRepo, seq),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )

            if (response) {
                this.props.navigate(`/resident/condominiums/${condominiumCd}/ridges/${ridgeCd}/rooms/${roomNumber}/users`)
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderDeleteDialog(): ReactElement {
        const {subUserFacilityReserveAvailability} = this.state
        const {familyName, firstName} = this.state.userInfo

        let message: string = Messages.IOPCC0011.toString(`${familyName} ${firstName}さん`)
        if (subUserFacilityReserveAvailability === AVAILABLE_OR_NOT.AVAILABLE.value) {
            message = message + '\n' + Messages.IOPCR0002.toString()
        }

        return (
            <NatResidentMessageDialogComponent
                open={this.state.openDeleteDialog}
                params={{
                    title: '削除確認',
                    titleIcon: <DeleteTitleIconWrapper><DeleteForever/></DeleteTitleIconWrapper>,
                    errorLevel: Messages.IOPCC0011.errorLevel,
                    message: message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: '削除する',
                            buttonHandler: this.handleDeleteOkClick,
                            color: Colors.Alert
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelClick,
                        },
                    ],
                }}
            />
        )
    }

    renderUserOperation(): ReactElement {
        const {userInfo} = this.state

        return (
            <>
                {this.loginUserResidentUserType === RESIDENT_USER_TYPE.MAIN.value &&
                    userInfo.residentUserType === RESIDENT_USER_TYPE.SUB &&
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_RESIDENT_USER}>
                        <BlockSpacer height={'15px'}/>
                        <ItemHeading>ユーザの操作</ItemHeading>
                        <WideWhiteArea>
                            <DeleteUserButton onClick={this.handleDeleteClick}>
                                <LeftRowFlexContainer>
                                    <DeleteForever style={deleteIconStyle}/>
                                    このユーザを削除
                                </LeftRowFlexContainer>
                            </DeleteUserButton>
                        </WideWhiteArea>
                    </AuthorityChecker>
                }
            </>
        )
    }

    renderSubUserAuthority(): ReactElement {
        const {userInfo, subUserAuthorityList} = this.state

        if (userInfo.residentUserType === RESIDENT_USER_TYPE.MAIN ||
            subUserAuthorityList.length === 0) {
            return <></>
        }

        return (
            <>
                <BlockSpacer height={'15px'}/>
                <ItemHeading>サブユーザの利用制限</ItemHeading>
                <WideWhiteArea>
                    <GridContainer>
                        <GridItem over={11} under={9}>
                            {subUserAuthorityList.map((subUserAuthority, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        <GridContainer>
                                            <GridItem over={4}>
                                                <DisplayItemInputTitleNoMargin>
                                                    {subUserAuthority.authorityName}
                                                </DisplayItemInputTitleNoMargin>
                                            </GridItem>
                                            <GridItem over={8}>
                                                <Box fontWeight={FontWeights.Bold}>{subUserAuthority.valid.label}</Box>
                                            </GridItem>
                                        </GridContainer>
                                        {subUserAuthorityList.length - 1 !== index &&
                                            <>
                                                <BlockSpacerSpOnly height={'30px'}/>
                                                <BlockSpacerPcOnly height={'10px'}/>
                                            </>
                                        }
                                    </React.Fragment>
                                )
                            })}
                        </GridItem>
                        <GridItem over={1} under={3}>
                            {this.loginUserResidentUserType === RESIDENT_USER_TYPE.MAIN.value &&
                                <PartialAuthorityChecker permittedAuthorities={[
                                    HKConstants.CMN.AUTH.RESTRICTION_SETTING_BILLING_AMOUNT,
                                    HKConstants.CMN.AUTH.RESTRICTION_SETTING_ACCOUNT_INFORMATION
                                ]}>
                                    <ChangeButton onClick={this.changeAuthorityButtonClick}>変更</ChangeButton>
                                </PartialAuthorityChecker>
                            }
                        </GridItem>
                    </GridContainer>
                </WideWhiteArea>
            </>
        )
    }

    renderUserInfo(): ReactElement {
        const {
            userId,
            familyName,
            firstName,
            mailAddressPc,
            mailAddressMb,
            isSelf,
            residentUserType,
        } = this.state.userInfo

        return (
            <WideWhiteArea>
                <Box>
                    <Box fontWeight={FontWeights.Bold} component={'span'} pr={'10px'}>{familyName} {firstName}</Box>
                    {isSelf && <Box component={'span'}>（あなた）</Box>}
                </Box>
                <UserRole>
                    {
                        residentUserType === RESIDENT_USER_TYPE.MAIN &&
                        <PrimaryTag>{residentUserType.label}</PrimaryTag> ||
                        residentUserType === RESIDENT_USER_TYPE.SUB &&
                        <SecondaryTag>{residentUserType.label}</SecondaryTag>
                    }
                    {userId === this.state.unitOwner?.userId &&
                        <SecondaryTag>区分所有者</SecondaryTag>
                    }
                </UserRole>
                {mailAddressPc && <Box pt={'10px'}>{mailAddressPc}</Box>}
                {mailAddressMb && <Box pt={'10px'}>{mailAddressMb}</Box>}
            </WideWhiteArea>
        )
    }

    render(): ReactElement {
        const {isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return <></>
        }

        return (<>
            {this.renderDeleteDialog()}
            <PlainLinkStyledBackButton onClick={this.backButtonClick}>
                戻る
            </PlainLinkStyledBackButton>
            <BlockSpacer height={'14px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>ユーザ詳細</StyledPageTitle>
            <Box height={'20px'}/>
            {this.renderUserInfo()}
            {this.renderSubUserAuthority()}
            {this.renderUserOperation()}
            <BlockSpacer height={'20px'}/>
            <StyledSecondaryButton onClick={this.backButtonClick}>
                戻る
            </StyledSecondaryButton>
        </>)
    }
}

export default createResidentWithErrorHandlingPage(NatResidentUserDetailPage)

const WideWhiteArea = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  padding: 10px 15px;
  box-sizing: border-box;
  overflow: hidden;
`

const UserRole = styled.div`
  line-height: 1.5;
  display: flex;
  gap: 10px;
  margin-top: 10px;
`

const StyledTag = styled.div`
  font-size: ${FontSize.Middle};
  color: ${Colors.AccentDark};
  border-radius: 4px;
  padding: 2px 10px;
`

const PrimaryTag = styled(StyledTag)`
  background: rgba(242, 248, 251, 0.7);
`
const SecondaryTag = styled(StyledTag)`
  background: rgba(242, 242, 242, 1);
`

const ItemHeading = styled.div`
  background-color: ${Colors.AccentLight};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  font-size: ${FontSize.Body};
  padding: 6px 15px 6px;
  box-sizing: border-box;
  overflow: hidden;
`

const ChangeButton = styled(StyledSecondaryButton)`
  height: 36px;
  width: 70px;
`

const DeleteUserButton = styled(UnstyledLinkStyledButton)`
  color: ${Colors.Alert};
  line-height: 24px;
  padding: 11.5px 0;
`

const DeleteTitleIconWrapper = styled.div`
  line-height: 1;

  svg {
    color: ${Colors.Alert};
    font-size: ${FontSize.ExtraBig} !important;
    ${currentMedia.lessThan('sp')`
      font-size: ${FontSize.Big}!important;
   `};
  }
`

const deleteIconStyle = {
    fontSize: FontSize.Big,
    color: Colors.Alert,
    marginRight: '20px',
}
